import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatMoney, formatDate, formatPercent, getCurrency } from "@/lib/prefs";
import { computeRecipeCost } from "@/lib/units";
import { Link } from "react-router-dom";
import { ArrowRight, Boxes, BookOpen, TrendingUp, Utensils, Loader2, Users, Calendar, CircleDollarSign, Plug } from "lucide-react";
import LaborPerRecipeChart from "@/components/LaborPerRecipeChart";
import { avgHourlyRate, totalLaborCost, isoDate, startOfWeek, addDays } from "@/lib/labor";
import OwnerReport from "@/components/OwnerReport";
import SalesFoodCostScatter from "@/components/LiveScatterCanvas";
import { DashboardSkeleton } from "@/components/GlassSkeleton";
import KitchenAlertsBanner from "@/components/KitchenAlertsBanner";
import { useModuleTour, TourButton } from "@/components/useModuleTour";

// Today boundary in the visitor's local timezone as an ISO string, so the
// POS revenue window matches how a chef reads their own day (midnight to
// now) instead of drifting by UTC offset.
function todayStartIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function StatCard({ label, value, sub, testId, icon: Icon }) {
  // Mobile-safe KPI tile. The label wraps to two lines instead of getting
  // truncated ("INGREDI...") because on a 375px phone the 2-col grid gives
  // each card ~170px inside padding. The value uses a fluid clamp so long
  // AUD strings like A$5,777 fit without ellipsing, and only NBSP the
  // currency symbol to the amount so line breaks land in a sensible spot.
  return (
    <div className="rounded-2xl glass-card p-4 card-hover overflow-hidden" data-testid={testId}>
      <div className="flex items-start justify-between mb-2 gap-2">
        <div
          className="label-eyebrow leading-tight break-words"
          style={{ fontSize: "clamp(0.62rem, 2.3vw, 0.72rem)", letterSpacing: "0.14em" }}
        >
          {label}
        </div>
        {Icon && <Icon className="w-[1.05rem] h-[1.05rem] text-muted-foreground shrink-0 mt-0.5" strokeWidth={1.75} />}
      </div>
      <div
        className="font-display tabular leading-none break-words"
        style={{
          fontSize: "clamp(1.35rem, 5.6vw, 1.9rem)",
          fontWeight: 800,
          letterSpacing: "-0.015em",
          wordBreak: "break-word",
        }}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </div>
      {sub && (
        <div
          className="text-muted-foreground mt-2 line-clamp-2 leading-snug"
          style={{ fontSize: "clamp(0.68rem, 2.4vw, 0.82rem)" }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [salesSummary, setSalesSummary] = useState(null);
  const [ownerReport, setOwnerReport] = useState(null);
  const [posToday, setPosToday] = useState({ revenue: 0, count: 0, providers: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const weekStart = isoDate(startOfWeek(new Date()));
        const weekEnd = isoDate(addDays(startOfWeek(new Date()), 6));
        const [i, r, s, sh, ss, or, ps] = await Promise.all([
          api.get("/ingredients"),
          api.get("/recipes"),
          api.get("/staff"),
          api.get(`/shifts?date_from=${weekStart}&date_to=${weekEnd}`),
          api.get("/sales/summary?days=7"),
          api.get("/report/owner?days=7"),
          api.get("/pos/sales", { params: { since: todayStartIso(), limit: 500 } }).catch(() => ({ data: [] })),
        ]);
        setIngredients(i.data);
        setRecipes(r.data);
        setStaff(s.data);
        setShifts(sh.data);
        setSalesSummary(ss.data);
        setOwnerReport(or.data);
        const rows = ps.data || [];
        const revenue = rows.reduce((sum, row) => sum + (Number(row.price) || 0) * (Number(row.quantity) || 0), 0);
        const providers = new Set(rows.map((r) => r.provider)).size;
        setPosToday({ revenue, count: rows.length, providers });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const ingredientsById = useMemo(
    () => Object.fromEntries(ingredients.map((i) => [i.id, i])),
    [ingredients]
  );

  const costed = useMemo(
    () =>
      recipes.map((r) => ({
        recipe: r,
        cost: computeRecipeCost({ recipe: r, ingredientsById }),
      })),
    [recipes, ingredientsById]
  );

  const totals = useMemo(() => {
    if (!costed.length) return { avgFoodCostPct: 0, avgMargin: 0, topRecipe: null };
    const withSell = costed.filter((c) => c.cost.sellPrice > 0);
    const avg = withSell.length
      ? withSell.reduce((s, c) => s + c.cost.foodCostPercent, 0) / withSell.length
      : 0;
    const avgMargin = withSell.length
      ? withSell.reduce((s, c) => s + c.cost.margin, 0) / withSell.length
      : 0;
    const top = [...costed].sort((a, b) => b.cost.margin - a.cost.margin)[0];
    return { avgFoodCostPct: avg, avgMargin, topRecipe: top };
  }, [costed]);

  const dashboardTourSteps = useMemo(() => ([
    {
      selector: '[data-testid="stat-food-cost"]',
      title: "Your food cost at a glance",
      body: "This is the average food cost percentage across every priced recipe. Keeping this under 32% is a healthy fine-dining target.",
      placement: "bottom",
    },
    {
      selector: '[data-testid="stat-pos-today"]',
      title: "Live POS revenue",
      body: "Once you connect a POS provider, today's revenue and margin land here in real time.",
      placement: "bottom",
    },
    {
      selector: '[data-testid="sales-fc-scatter"]',
      title: "Menu efficiency plot",
      body: "Every dot is a plate. Higher sales and lower food cost is the sweet spot in the top-left.",
      placement: "top",
    },
    {
      selector: '[data-testid="nav-recipes"]',
      title: "Recipes live here",
      body: "Cost, portion and price every plate. When suppliers move a price, we recompute margins instantly.",
      placement: "right",
    },
  ]), []);
  const dashTour = useModuleTour("dashboard_v1", dashboardTourSteps);

  if (loading) {
    return <DashboardSkeleton />;
  }

  const foodRecipes = costed.filter((c) => c.recipe.bracket === "food");
  const drinkRecipes = costed.filter((c) => c.recipe.bracket === "drink");
  const staffById = Object.fromEntries(staff.map((s) => [s.id, s]));
  const weeklyLabor = totalLaborCost(shifts, staffById);
  const avgRate = avgHourlyRate(staff);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto space-y-8" data-testid="dashboard">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">{formatDate(new Date())}</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">
            Good day, {user?.name?.split(" ")[0] || "Chef"}.
          </h1>
          <p className="text-muted-foreground mt-2">
            Costs shown in {getCurrency()} • locale auto-detected.
          </p>
        </div>
        <TourButton onClick={dashTour.start} testId="dashboard-tour-btn" />
      </header>

      {dashTour.TourNode}

      <KitchenAlertsBanner />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
        <StatCard
          label="Ingredients"
          value={ingredients.length}
          sub="In pantry"
          icon={Boxes}
          testId="stat-ingredients"
        />
        <StatCard
          label="Recipes"
          value={recipes.length}
          sub={`${foodRecipes.length} food • ${drinkRecipes.length} drink`}
          icon={BookOpen}
          testId="stat-recipes"
        />
        <StatCard
          label="Avg food cost"
          value={formatPercent(totals.avgFoodCostPct, 1)}
          sub="Priced recipes"
          icon={TrendingUp}
          testId="stat-food-cost"
        />
        <StatCard
          label="Avg margin"
          value={formatMoney(totals.avgMargin)}
          sub="Per serving"
          icon={Utensils}
          testId="stat-margin"
        />
        <StatCard
          label="POS today"
          value={formatMoney(posToday.revenue)}
          sub={posToday.count === 0 ? "No sales yet today" : `${posToday.count} sale${posToday.count === 1 ? "" : "s"} across ${posToday.providers} provider${posToday.providers === 1 ? "" : "s"}`}
          icon={Plug}
          testId="stat-pos-today"
        />
        <StatCard
          label="Week labor"
          value={formatMoney(weeklyLabor)}
          sub={`${shifts.length} shifts`}
          icon={Calendar}
          testId="stat-labor"
        />
        <StatCard
          label="Active staff"
          value={staff.filter((s) => s.active).length}
          sub={`Avg ${formatMoney(avgRate)}/h`}
          icon={Users}
          testId="stat-staff"
        />
      </div>

      <LaborPerRecipeChart recipes={recipes} ingredientsById={ingredientsById} avgRate={avgRate || 25} />

      <SalesFoodCostScatter days={7} />

      <OwnerReport data={ownerReport} />

      {salesSummary && (
        <section data-testid="sales-summary">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="label-eyebrow gold">Sales • last 7 days</div>
              <h2 className="font-display text-2xl mt-1">Real-time margins</h2>
            </div>
            <Link to="/recipes" className="text-sm gold hover:underline inline-flex items-center gap-1" data-testid="link-sell">
              Record a sale <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard label="Units sold" value={salesSummary.totals.units_sold} icon={CircleDollarSign} testId="stat-units-sold" />
            <StatCard label="Revenue" value={formatMoney(salesSummary.totals.revenue)} testId="stat-revenue" />
            <StatCard label="Food cost" value={formatMoney(salesSummary.totals.food_cost)} testId="stat-food-cost-7d" />
            <StatCard label="Labor cost" value={formatMoney(salesSummary.totals.labor_cost)} testId="stat-labor-7d" />
            <StatCard label="Margin" value={formatMoney(salesSummary.totals.margin)} sub="Live P&L" testId="stat-margin-7d" />
          </div>
          {salesSummary.by_recipe.length > 0 && (
            <div className="mt-4 rounded-2xl glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead><tr className="text-left border-b border-border">
                    <th className="px-4 py-3 label-eyebrow">Recipe</th>
                    <th className="px-4 py-3 label-eyebrow text-right">Units</th>
                    <th className="px-4 py-3 label-eyebrow text-right">Revenue</th>
                    <th className="px-4 py-3 label-eyebrow text-right">Cost</th>
                    <th className="px-4 py-3 label-eyebrow text-right">Margin</th>
                  </tr></thead>
                  <tbody>
                    {salesSummary.by_recipe.map((r) => (
                      <tr key={r.recipe_id} className="border-b border-border/60" data-testid={`sales-row-${r.recipe_id}`}>
                        <td className="px-4 py-3 font-medium">{r.recipe_name}</td>
                        <td className="px-4 py-3 text-right tabular">{r.units_sold}</td>
                        <td className="px-4 py-3 text-right tabular">{formatMoney(r.revenue)}</td>
                        <td className="px-4 py-3 text-right tabular text-muted-foreground">{formatMoney(r.food_cost + r.labor_cost)}</td>
                        <td className="px-4 py-3 text-right tabular font-medium gold">{formatMoney(r.margin)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl">Recipe P&L</h2>
          <Link
            to="/recipes"
            className="text-sm font-medium text-accent hover:underline inline-flex items-center gap-1"
            data-testid="link-view-all-recipes"
          >
            All recipes <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="rounded-2xl glass-card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-secondary/50">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow font-bold">Recipe</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Cost / serving</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Sell</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Food %</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {costed.map(({ recipe, cost }) => (
                <tr key={recipe.id} className="border-t border-border/60 hover:bg-secondary/30">
                  <td className="px-4 py-3">
                    <Link to={`/recipes/${recipe.id}`} className="hover:underline" data-testid={`dash-recipe-${recipe.id}`}>
                      <div className="font-medium">{recipe.name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{recipe.bracket}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-right tabular">{formatMoney(cost.perServing)}</td>
                  <td className="px-4 py-3 text-right tabular">{formatMoney(cost.sellPrice)}</td>
                  <td className="px-4 py-3 text-right tabular">
                    <span
                      className={
                        cost.foodCostPercent > 35
                          ? "text-destructive font-medium"
                          : cost.foodCostPercent > 0
                          ? "text-[hsl(var(--success))] font-medium"
                          : "text-muted-foreground"
                      }
                    >
                      {cost.sellPrice > 0 ? formatPercent(cost.foodCostPercent, 1) : "-"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular font-medium">{formatMoney(cost.margin)}</td>
                </tr>
              ))}
              {!costed.length && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No recipes yet. Create your first one to see the math come alive.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </section>
    </div>
  );
}
